/** Image and gallery fields: upload (resized + WebP in the browser), drag & drop, pick from the library, reorder. */
import { useRef, useState, type DragEvent } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { basename, isVideoUrl, type Field } from '@hotpot/core';
import { useT } from '../i18n.js';
import { ImageError } from '../lib/images.js';
import { useSite, useToast } from '../state.js';
import { errorText } from '../screens/Login.js';
import { Dialog } from '../components/ui.js';
import { useEditor } from './context.js';

type ImageField = Extract<Field, { type: 'image' }>;
type GalleryField = Extract<Field, { type: 'gallery' }>;

function useUpload(field: ImageField | GalleryField) {
  const ctx = useEditor();
  const toast = useToast();
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const run = async (files: File[]): Promise<string[]> => {
    if (!files.length) return [];
    setBusy(true);
    try {
      return await ctx.upload(files, field);
    } catch (e) {
      if (e instanceof ImageError) toast(e.code === 'too_big' ? t('imageTooBig', { name: e.fileName, mb: e.limitMb }) : t('notAnImage', { name: e.fileName }));
      else toast((e as Error).message);
      return [];
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

const isVideoFile = (f: File) => f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(f.name);

function filesFrom(e: DragEvent, videos = false): File[] {
  return [...(e.dataTransfer?.files ?? [])].filter((f) => f.type.startsWith('image/') || /\.(svg|heic)$/i.test(f.name) || (videos && isVideoFile(f)));
}

export function ImageControl({ field, value, onChange }: { field: ImageField; value: string | undefined; onChange: (v: string | undefined) => void }) {
  const { t } = useT();
  const ctx = useEditor();
  const input = useRef<HTMLInputElement>(null);
  const { busy, run } = useUpload(field);
  const [drop, setDrop] = useState(false);
  const src = value ? ctx.imageSrc(value, field) : undefined;
  return (
    <div
      className="img-field"
      onDragOver={(e) => {
        e.preventDefault();
        setDrop(true);
      }}
      onDragLeave={() => setDrop(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setDrop(false);
        const [v] = await run(filesFrom(e).slice(0, 1));
        if (v) onChange(v);
      }}
    >
      <div className={`img-box${drop ? ' drop' : ''}${value?.endsWith('.svg') ? ' checker' : ''}`}>
        {src ? <img src={src} alt="" style={value?.endsWith('.svg') ? { objectFit: 'contain', padding: 12 } : undefined} /> : <span className="muted small" style={{ display: 'grid', placeContent: 'center', height: '100%' }}>{busy ? t('processing') : t('noImage')}</span>}
      </div>
      <div className="img-actions">
        {value && <span className="path">{basename(value)}</span>}
        <button type="button" className="outline-btn" onClick={() => input.current?.click()} disabled={busy}>
          {busy ? t('processing') : value ? t('replace') : t('upload')}
        </button>
        <button
          type="button"
          className="link-btn"
          onClick={async () => {
            const [v] = (await ctx.pick(field, false)) ?? [];
            if (v) onChange(v);
          }}
        >
          {t('chooseFromLibrary')}
        </button>
        {value && (
          <button type="button" className="link-btn danger" onClick={() => onChange(undefined)}>
            {t('remove')}
          </button>
        )}
        <input
          ref={input}
          type="file"
          accept={field.accept ?? 'image/*'}
          hidden
          onChange={async (e) => {
            const [v] = await run([...(e.target.files ?? [])].slice(0, 1));
            e.target.value = '';
            if (v) onChange(v);
          }}
        />
      </div>
    </div>
  );
}

export function GalleryControl({ field, value, onChange }: { field: GalleryField; value: string[] | undefined; onChange: (v: string[]) => void }) {
  const { t } = useT();
  const ctx = useEditor();
  const input = useRef<HTMLInputElement>(null);
  const site = useSite();
  const toast = useToast();
  const { busy: imagesBusy, run: runImages } = useUpload(field);
  const [progress, setProgress] = useState<number | null>(null);
  const busy = imagesBusy || progress !== null;
  const storage = field.storage;
  /** Pictures are resized in the browser and committed; videos (only with `storage`) go to R2 and come back as URLs. */
  const run = async (files: File[]): Promise<string[]> => {
    const videos = storage ? files.filter(isVideoFile) : [];
    const pictures = files.filter((f) => !videos.includes(f));
    const out: string[] = [...(await runImages(pictures))];
    for (const file of videos) {
      setProgress(0);
      try {
        out.push(await site.handle.uploadLarge(storage!, file, setProgress));
      } catch (e) {
        toast(t('fileUploadFailed', { error: errorText(e, t) }));
      } finally {
        setProgress(null);
      }
    }
    return out;
  };
  const [drop, setDrop] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const list = Array.isArray(value) ? value : [];
  const ids = list.map((v, i) => `${i}:${v}`);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    onChange(arrayMove(list, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id))));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="gallery">
          {list.map((v, i) => (
            <Tile key={ids[i]} id={ids[i]!} n={i + 1} src={isVideoUrl(v) ? undefined : ctx.imageSrc(v, field)} video={isVideoUrl(v) ? v : undefined} name={basename(v)} onEdit={() => setEditing(i)} editLabel={t('edit')} onRemove={() => onChange(list.filter((_, n) => n !== i))} removeLabel={t('remove')} />
          ))}
          <button
            type="button"
            className={`add${drop ? ' drop' : ''}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDrop(true);
            }}
            onDragLeave={() => setDrop(false)}
            onDrop={async (e) => {
              e.preventDefault();
              setDrop(false);
              const values = await run(filesFrom(e, !!storage));
              if (values.length) onChange([...list, ...values]);
            }}
            disabled={busy}
          >
            {progress !== null ? t('uploadingPct', { n: Math.round(progress * 100) }) : busy ? t('processing') : `+ ${t(storage ? 'dropMedia' : 'dropImages')}`}
          </button>
        </div>
      </SortableContext>
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          className="link-btn"
          onClick={async () => {
            const picked = await ctx.pick(field, true);
            if (picked?.length) onChange([...list, ...picked]);
          }}
        >
          {t('chooseFromLibrary')}
        </button>
      </div>
      {editing !== null && list[editing] !== undefined && (
        <ItemDialog
          field={field}
          value={list[editing]!}
          busy={busy}
          progress={progress}
          upload={run}
          onSave={(v) => {
            onChange(list.map((x, n) => (n === editing ? v : x)));
            setEditing(null);
          }}
          onRemove={() => {
            onChange(list.filter((_, n) => n !== editing));
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      <input
        ref={input}
        type="file"
        accept={field.accept ?? (storage ? 'image/*,video/*,.mov,.mp4,.m4v,.webm' : 'image/*')}
        multiple
        hidden
        onChange={async (e) => {
          const values = await run([...(e.target.files ?? [])]);
          e.target.value = '';
          if (values.length) onChange([...list, ...values]);
        }}
      />
    </DndContext>
  );
}

/** The first frame of a video as a still: the browser shows it for `#t=0.1` once the metadata is loaded. */
function VideoStill({ url, className }: { url: string; className?: string }) {
  return <video className={className} src={`${url}#t=0.1`} muted playsInline preload="metadata" disablePictureInPicture tabIndex={-1} />;
}

function Tile({ id, n, src, video, name, onEdit, editLabel, onRemove, removeLabel }: { id: string; n: number; src?: string; video?: string; name: string; onEdit: () => void; editLabel: string; onRemove: () => void; removeLabel: string }) {
  const s = useSortable({ id });
  return (
    <div className="tile" ref={s.setNodeRef} style={{ transform: CSS.Transform.toString(s.transform), transition: s.transition, zIndex: s.isDragging ? 2 : undefined }} {...s.attributes} {...s.listeners} title={name}>
      {src && <img src={src} alt={name} loading="lazy" />}
      {video && <VideoStill url={video} />}
      {video && (
        <span className="play-badge" aria-hidden="true">
          ▶
        </span>
      )}
      <span className="n">{n}</span>
      <button type="button" className="edit" onPointerDown={(e) => e.stopPropagation()} onClick={onEdit} aria-label={`${editLabel} ${name}`} title={editLabel}>
        <svg width="16" height="4" viewBox="0 0 16 4" aria-hidden="true">
          <circle cx="2" cy="2" r="1.6" fill="currentColor" />
          <circle cx="8" cy="2" r="1.6" fill="currentColor" />
          <circle cx="14" cy="2" r="1.6" fill="currentColor" />
        </svg>
      </button>
      <button type="button" className="x" onPointerDown={(e) => e.stopPropagation()} onClick={onRemove} aria-label={`${removeLabel} ${name}`}>
        ✕
      </button>
    </div>
  );
}

/** Edit one item of the gallery: paste a video link, upload another file, pick a picture from the library or remove it. */
function ItemDialog({ field, value, busy, progress, upload, onSave, onRemove, onClose }: { field: GalleryField; value: string; busy: boolean; progress: number | null; upload: (files: File[]) => Promise<string[]>; onSave: (v: string) => void; onRemove: () => void; onClose: () => void }) {
  const { t } = useT();
  const ctx = useEditor();
  const file = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);
  const trimmed = draft.trim();
  const video = isVideoUrl(trimmed);
  const accept = field.accept ?? (field.storage ? 'image/*,video/*,.mov,.mp4,.m4v,.webm' : 'image/*');
  const valid = !!trimmed && (video || !/^https?:\/\//i.test(trimmed) || /^https:\/\//i.test(trimmed));
  return (
    <Dialog
      title={t('galleryItemTitle')}
      onClose={onClose}
      buttons={
        <>
          <button type="button" className="link-btn danger" onClick={onRemove}>
            {t('remove')}
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="link-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn" onClick={() => onSave(trimmed)} disabled={!valid || busy}>
            {t('save')}
          </button>
        </>
      }
    >
      <div className="item-preview">{video ? <VideoStill url={trimmed} /> : <img src={ctx.imageSrc(trimmed, field)} alt="" />}</div>
      <p className="item-name">{basename(trimmed)}</p>
      {field.storage && (
        <>
          <label className="label" htmlFor="item-link">
            {t('videoLink')}
          </label>
          <input id="item-link" className="input" type="url" inputMode="url" value={/^https?:\/\//i.test(draft) ? draft : ''} placeholder={t('fileUrlPlaceholder')} onChange={(e) => setDraft(e.target.value)} />
        </>
      )}
      <div className="item-actions">
        <button type="button" className="outline-btn" onClick={() => file.current?.click()} disabled={busy}>
          {progress !== null ? t('uploadingPct', { n: Math.round(progress * 100) }) : busy ? t('processing') : t('uploadFile')}
        </button>
        <button
          type="button"
          className="link-btn"
          onClick={async () => {
            const [v] = (await ctx.pick(field, false)) ?? [];
            if (v) setDraft(v);
          }}
        >
          {t('chooseFromLibrary')}
        </button>
        <input
          ref={file}
          type="file"
          accept={accept}
          hidden
          onChange={async (e) => {
            const [v] = await upload([...(e.target.files ?? [])].slice(0, 1));
            e.target.value = '';
            if (v) setDraft(v);
          }}
        />
      </div>
    </Dialog>
  );
}
